// Agent Command — approval queue panel (commit_2).
//
// Reads REAL sq_agent_action_requests via useAgentActionQueue (owned by
// the route wrapper). Approve/Reject call the exact typed
// api.agentWorkspace.approve(request_id, decision, note) — no
// client-side re-implementation of the self-approval rule or the
// atomic pending_approval transition; both are enforced server-side
// (api/square/agent-workspace/approve.ts, PR #129). This panel only
// renders what the server already decided, plus an honest UX hint
// layered on top of (never instead of) that enforcement.

import type { CSSProperties } from 'react'
import type { RegistryAgent } from '../../../api/client'
import type { AgentWorkspaceLoadState, AgentWorkspaceSummary } from './useAgentWorkspaces'
import type { AgentActionRequestRow, DecisionState, QueueLoadState } from './useAgentActionQueue'
import { AGENT_CARD, AGENT_FONT, AGENT_STATUS_ACCENT, AGENT_VOID, agentActionStatusAccent } from './theme'
import { AGENT_ACTION_DECIDE_MIN_LEVEL } from './permissions'

export interface AgentApprovalQueueProps {
  agents: RegistryAgent[]
  currentUserId: string | null
  roleLevel: number | null

  workspaceState: AgentWorkspaceLoadState
  workspaceErrorMessage: string | null
  workspaces: AgentWorkspaceSummary[]

  queueState: QueueLoadState
  queueErrorMessage: string | null
  rows: AgentActionRequestRow[]
  decisions: Record<string, DecisionState>
  onDecide: (requestId: string, decision: 'approved' | 'rejected') => void
  onReload: () => void
}

function shortId(id: string): string {
  return id.length > 10 ? `${id.slice(0, 8)}…` : id
}

function agentLabel(agentId: string | null, agents: RegistryAgent[]): string {
  if (!agentId) return 'operator (no preparing agent)'
  const agent = agents.find((a) => a.agent_id === agentId)
  return agent ? agent.display_name : agentId
}

const bodyText: CSSProperties = { color: AGENT_VOID.muted, fontSize: 12.5, lineHeight: 1.6 }

const btnBase: CSSProperties = {
  fontFamily: AGENT_FONT.mono,
  fontSize: 9.5,
  fontWeight: 800,
  letterSpacing: '0.06em',
  textTransform: 'uppercase',
  padding: '6px 12px',
  borderRadius: 7,
  border: '1px solid transparent',
  cursor: 'pointer',
}

function decisionNote(decision: DecisionState | undefined): { text: string; accent: string } | null {
  if (!decision || decision.status === 'idle' || decision.status === 'deciding') return null
  if (decision.status === 'self_decision_forbidden') {
    return { text: decision.message ?? 'The request creator cannot decide their own request.', accent: AGENT_STATUS_ACCENT.violet }
  }
  if (decision.status === 'insufficient_level') {
    return { text: decision.message ?? `Requires role level ${AGENT_ACTION_DECIDE_MIN_LEVEL}.`, accent: AGENT_STATUS_ACCENT.amber }
  }
  if (decision.status === 'already_decided') {
    return { text: decision.message ?? 'Someone else already decided this request.', accent: AGENT_STATUS_ACCENT.amber }
  }
  if (decision.status === 'error') {
    return { text: decision.message ?? 'Decision failed.', accent: AGENT_STATUS_ACCENT.red }
  }
  return null
}

function QueueRow({
  row,
  agents,
  currentUserId,
  roleLevel,
  decision,
  onDecide,
}: {
  row: AgentActionRequestRow
  agents: RegistryAgent[]
  currentUserId: string | null
  roleLevel: number | null
  decision: DecisionState | undefined
  onDecide: (decision: 'approved' | 'rejected') => void
}) {
  const accent = agentActionStatusAccent(row.approval_status, row.execution_status)
  const pending = row.approval_status === 'pending_approval'
  const deciding = decision?.status === 'deciding'
  const isSelf = !!currentUserId && !!row.requested_by && row.requested_by === currentUserId
  const belowLevel = roleLevel !== null && roleLevel < AGENT_ACTION_DECIDE_MIN_LEVEL
  const note = decisionNote(decision)

  let disabledReason: string | null = null
  if (deciding) disabledReason = 'Deciding…'
  else if (belowLevel) disabledReason = `Requires role level ${AGENT_ACTION_DECIDE_MIN_LEVEL} — you are level ${roleLevel}.`
  else if (isSelf) disabledReason = 'You created this request — a different approver is required.'

  return (
    <div style={{ ...AGENT_CARD, display: 'flex', flexDirection: 'column', gap: 8 }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap' }}>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontFamily: AGENT_FONT.display, fontWeight: 600, fontSize: 13.5, color: AGENT_VOID.ink }}>{row.title}</div>
          <div style={{ fontFamily: AGENT_FONT.mono, fontSize: 8.5, color: AGENT_VOID.faint, marginTop: 3 }}>
            {row.action_type} · {row.workspace_name} · agent: {agentLabel(row.prepared_by_agent_id, agents)} · requested by{' '}
            <span title={row.requested_by ?? undefined}>{row.requested_by ? shortId(row.requested_by) : 'unknown'}</span>
          </div>
        </div>
        <span
          style={{
            fontFamily: AGENT_FONT.mono,
            fontSize: 7.5,
            fontWeight: 800,
            letterSpacing: '0.04em',
            padding: '2px 7px',
            borderRadius: 5,
            border: `1px solid ${accent}66`,
            color: accent,
            background: `${accent}12`,
            whiteSpace: 'nowrap',
            textTransform: 'uppercase',
          }}
        >
          {row.approval_status} · {row.execution_status}
        </span>
      </div>

      {row.summary && <div style={{ fontSize: 11.5, color: AGENT_VOID.muted, lineHeight: 1.5 }}>{row.summary}</div>}

      {row.rejection_reason && (
        <div style={{ fontSize: 11, color: AGENT_STATUS_ACCENT.red }}>Rejection reason: {row.rejection_reason}</div>
      )}

      {pending && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <button
            type="button"
            onClick={() => onDecide('approved')}
            disabled={!!disabledReason}
            title={disabledReason ?? 'Approve and execute through the owning gate'}
            aria-describedby={disabledReason ? `agent-queue-disabled-reason-${row.id}` : undefined}
            style={{
              ...btnBase,
              color: disabledReason ? AGENT_VOID.faint : '#04140c',
              background: disabledReason ? 'rgba(150,140,220,0.08)' : AGENT_STATUS_ACCENT.green,
              opacity: disabledReason ? 0.55 : 1,
              cursor: disabledReason ? 'not-allowed' : 'pointer',
            }}
          >
            Approve
          </button>
          <button
            type="button"
            onClick={() => onDecide('rejected')}
            disabled={!!disabledReason}
            title={disabledReason ?? 'Reject — nothing executes'}
            aria-describedby={disabledReason ? `agent-queue-disabled-reason-${row.id}` : undefined}
            style={{
              ...btnBase,
              color: disabledReason ? AGENT_VOID.faint : AGENT_STATUS_ACCENT.red,
              background: disabledReason ? 'rgba(150,140,220,0.08)' : `${AGENT_STATUS_ACCENT.red}12`,
              border: `1px solid ${disabledReason ? AGENT_VOID.hairline : AGENT_STATUS_ACCENT.red}66`,
              opacity: disabledReason ? 0.55 : 1,
              cursor: disabledReason ? 'not-allowed' : 'pointer',
            }}
          >
            Reject
          </button>
          {disabledReason && (
            <span id={`agent-queue-disabled-reason-${row.id}`} style={{ fontFamily: AGENT_FONT.mono, fontSize: 8.5, color: AGENT_VOID.faint }}>
              {disabledReason}
            </span>
          )}
        </div>
      )}

      {note && (
        <div style={{ fontFamily: AGENT_FONT.mono, fontSize: 9, color: note.accent, lineHeight: 1.5 }}>{note.text}</div>
      )}
    </div>
  )
}

export default function AgentApprovalQueue({
  agents,
  currentUserId,
  roleLevel,
  workspaceState,
  workspaceErrorMessage,
  workspaces,
  queueState,
  queueErrorMessage,
  rows,
  decisions,
  onDecide,
  onReload,
}: AgentApprovalQueueProps) {
  // A single, persistently-rendered aria-live wrapper (below) announces
  // every workspace/queue load-state transition — including this
  // component's own workspace-level early states — to assistive tech.
  // Building the body as one variable (rather than returning a
  // different root element per branch) keeps that one wrapper stable
  // across every state change instead of unmounting/remounting it.
  let body: JSX.Element

  if (workspaceState === 'loading') {
    body = <p style={bodyText}>Loading your Agent Workspace rooms…</p>
  } else if (workspaceState === 'auth_required') {
    body = <p style={bodyText}>Sign in to view the approval queue.</p>
  } else if (workspaceState === 'forbidden') {
    body = <p style={bodyText}>Your role does not have access to Agent Workspace.</p>
  } else if (workspaceState === 'error') {
    body = (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, alignItems: 'flex-start' }}>
        <p style={{ ...bodyText, color: AGENT_STATUS_ACCENT.red }}>Could not load your Agent Workspace rooms: {workspaceErrorMessage}</p>
        <button type="button" onClick={onReload} style={{ ...btnBase, color: AGENT_VOID.ink, background: 'rgba(150,140,220,0.1)', border: `1px solid ${AGENT_VOID.hairline}` }}>
          Retry
        </button>
      </div>
    )
  } else if (workspaces.length === 0) {
    body = (
      <p style={bodyText}>
        No Agent Workspace exists yet. Prepare your first request above — it creates one automatically — or create one
        directly from Agent Workspace.
      </p>
    )
  } else {
    const viewingText = `Viewing ${workspaces.length} workspace${workspaces.length === 1 ? '' : 's'}: ${workspaces.map((w) => w.name).join(', ')}`
    body = (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        <div style={{ fontFamily: AGENT_FONT.mono, fontSize: 8.5, color: AGENT_VOID.faint }}>{viewingText}</div>

        {queueState === 'loading' && <p style={bodyText}>Loading approval queue…</p>}
        {queueState === 'auth_required' && <p style={bodyText}>Sign in to view the approval queue.</p>}
        {queueState === 'forbidden' && <p style={bodyText}>Your role does not have access to this approval queue.</p>}
        {queueState === 'error' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, alignItems: 'flex-start' }}>
            <p style={{ ...bodyText, color: AGENT_STATUS_ACCENT.red }}>Could not load the approval queue: {queueErrorMessage}</p>
            <button type="button" onClick={onReload} style={{ ...btnBase, color: AGENT_VOID.ink, background: 'rgba(150,140,220,0.1)', border: `1px solid ${AGENT_VOID.hairline}` }}>
              Retry
            </button>
          </div>
        )}
        {queueState === 'ready' && rows.length === 0 && <p style={bodyText}>No agent action requests yet.</p>}

        {queueState === 'ready' && rows.length > 0 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {rows.map((row) => (
              <QueueRow
                key={row.id}
                row={row}
                agents={agents}
                currentUserId={currentUserId}
                roleLevel={roleLevel}
                decision={decisions[row.id]}
                onDecide={(decision) => onDecide(row.id, decision)}
              />
            ))}
          </div>
        )}
      </div>
    )
  }

  return (
    <div aria-live="polite" aria-atomic="false">
      {body}
    </div>
  )
}
